/* PlanComparison v3 — Netflix-style hero with one plan visible at a time.
 *
 * Switcher Free ⇄ Pro at top. Below: one luminous hero card animated transition.
 * Each card shows: hero title, pricing emphasis, 4-line summary (not full list), CTA.
 *
 * Variants:
 *   - landing: full hero
 *   - inline:  compact Pro-only upsell (same as before)
 */
import React, { useState } from "react";
import { motion, AnimatePresence, LayoutGroup } from "framer-motion";
import { Sparkles, Crown, ShieldCheck, Check } from "lucide-react";
import { Link } from "react-router-dom";

const FREE_SUMMARY = [
  "Mappa con pericoli, vet e dog park",
  "Karma, classifica città e sfide",
  "Scheda cane con passaporto digitale",
  "Vedi proprietari e missioni in zona",
];

const PRO_SUMMARY = [
  "Tutto del piano Free, senza limiti",
  "SOS allerta cane smarrito + walking 20 km",
  "Promemoria, cassetto documenti e PDF veterinario",
  "Match, chat e missioni di salvataggio attive",
];

export default function PlanComparison({ variant = "landing", onSelectPro, onSelectFree }) {
  if (variant === "inline") return <InlineCard onSelectPro={onSelectPro} />;
  return <NetflixCard onSelectPro={onSelectPro} onSelectFree={onSelectFree} />;
}

function NetflixCard({ onSelectPro, onSelectFree }) {
  const [active, setActive] = useState("pro");

  return (
    <div data-testid="plan-comparison" className="w-full max-w-md mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className="text-center mb-5"
      >
        <div className="text-[10px] uppercase tracking-[0.32em] font-semibold text-[#FF7A1A] mb-2">
          Scegli il tuo piano
        </div>
        <h2 className="font-display text-[28px] sm:text-[34px] leading-[1.05] text-[#1A1A1A]">
          Inizia con 30 giorni gratis
        </h2>
        <p className="text-[12.5px] text-[#6B6B6B] mt-2">
          Cambia o disdici quando vuoi · nessuna carta richiesta
        </p>
      </motion.div>

      {/* Switcher */}
      <LayoutGroup>
        <div className="flex p-1.5 rounded-2xl bg-[#FBF7F1] border border-[#F0EBE3] mb-5 max-w-xs mx-auto">
          {[
            { id: "free", label: "Free", color: "#2DBA60" },
            { id: "pro", label: "Pro", color: "#FF7A1A" },
          ].map((t) => {
            const a = active === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setActive(t.id)}
                data-testid={`plan-switch-${t.id}`}
                className="relative flex-1 px-3 py-2.5 rounded-xl text-[13px] font-semibold transition-colors z-10"
                style={{ color: a ? "#FFFFFF" : "#6B6B6B" }}
              >
                {a && (
                  <motion.div
                    layoutId="plan-pill"
                    className="absolute inset-0 rounded-xl"
                    style={{
                      background: t.id === "pro"
                        ? "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)"
                        : "linear-gradient(135deg, #2DBA60 0%, #48D478 100%)",
                      boxShadow: t.id === "pro"
                        ? "0 8px 24px -6px rgba(255,122,26,0.55)"
                        : "0 8px 24px -6px rgba(45,186,96,0.45)",
                    }}
                    transition={{ type: "spring", stiffness: 380, damping: 30 }}
                  />
                )}
                <span className="relative z-10 inline-flex items-center justify-center gap-1.5">
                  {t.id === "pro" && a && <Crown className="h-3.5 w-3.5" />}
                  {t.label}
                </span>
              </button>
            );
          })}
        </div>
      </LayoutGroup>

      {/* Hero card — one at a time */}
      <AnimatePresence mode="wait">
        {active === "pro" ? (
          <ProCard key="pro" onSelectPro={onSelectPro} />
        ) : (
          <FreeCard key="free" onSelectFree={onSelectFree} />
        )}
      </AnimatePresence>
    </div>
  );
}

/* PRO — luminous, Netflix-style premium card */
function ProCard({ onSelectPro }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: 10 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.96, y: -8 }}
      transition={{ duration: 0.35 }}
      data-testid="plan-pro-card"
      className="relative rounded-[28px] overflow-hidden border-2 border-[#FF7A1A]"
      style={{
        background: "linear-gradient(155deg, #1A1A1A 0%, #2A1810 55%, #3D2208 100%)",
        boxShadow: "0 26px 60px -16px rgba(255,122,26,0.55), 0 4px 18px -4px rgba(0,0,0,0.20)",
      }}
    >
      {/* Animated aurora background */}
      <motion.div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        animate={{
          background: [
            "radial-gradient(60% 80% at 30% 20%, rgba(255,122,26,0.45) 0%, transparent 60%)",
            "radial-gradient(60% 80% at 70% 70%, rgba(244,184,60,0.45) 0%, transparent 60%)",
            "radial-gradient(60% 80% at 30% 20%, rgba(255,122,26,0.45) 0%, transparent 60%)",
          ],
        }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      />
      {/* Floating sparkles */}
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <motion.div
          key={i}
          aria-hidden
          className="absolute pointer-events-none rounded-full"
          initial={{ y: 0, opacity: 0 }}
          animate={{ y: -90, opacity: [0, 0.85, 0] }}
          transition={{ duration: 3.8, delay: i * 0.55, repeat: Infinity, ease: "easeOut" }}
          style={{
            left: `${10 + i * 13}%`,
            bottom: 16,
            width: 4 + (i % 3),
            height: 4 + (i % 3),
            background: i % 2 === 0 ? "#FF7A1A" : "#F4B83C",
            boxShadow: `0 0 12px ${i % 2 === 0 ? "#FF7A1A" : "#F4B83C"}`,
          }}
        />
      ))}

      <div className="relative z-10 p-6 sm:p-7 text-white">
        {/* Header with crown + trial badge */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <motion.div
              animate={{ rotate: [0, -10, 10, 0] }}
              transition={{ duration: 2, repeat: Infinity, repeatDelay: 1.6 }}
              className="h-9 w-9 rounded-xl flex items-center justify-center"
              style={{
                background: "linear-gradient(135deg, #FF7A1A, #F4B83C)",
                boxShadow: "0 6px 18px -4px rgba(255,122,26,0.6)",
              }}
            >
              <Crown className="h-5 w-5 text-white" strokeWidth={2.4} />
            </motion.div>
            <span className="font-display text-[20px] sm:text-[22px] tracking-tight">ORIO Pro</span>
          </div>
          <motion.div
            initial={{ scale: 0.9 }}
            animate={{ scale: [0.95, 1.05, 0.95] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider"
            style={{
              background: "linear-gradient(135deg, #2DBA60, #48D478)",
              color: "white",
              boxShadow: "0 4px 12px -3px rgba(45,186,96,0.55)",
            }}
          >
            30 giorni gratis
          </motion.div>
        </div>

        {/* Price */}
        <div className="flex items-baseline gap-2 mb-1">
          <span className="font-display text-[44px] sm:text-[52px] leading-none">€4,99</span>
          <span className="text-[14px] text-white/70">/ mese</span>
        </div>
        <div className="text-[12px] text-white/60 mb-6">
          Dopo il primo mese · disdici quando vuoi
        </div>

        {/* Summary (4 bullet) */}
        <ul className="space-y-2.5 mb-7">
          {PRO_SUMMARY.map((s, i) => (
            <motion.li
              key={i}
              initial={{ opacity: 0, x: -6 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 + i * 0.08 }}
              className="flex items-center gap-2.5 text-[13.5px]"
            >
              <div
                className="h-5 w-5 rounded-full flex items-center justify-center shrink-0"
                style={{
                  background: "linear-gradient(135deg, #FF7A1A, #F4B83C)",
                  boxShadow: "0 3px 10px -2px rgba(255,122,26,0.5)",
                }}
              >
                <Check className="h-3 w-3 text-white" strokeWidth={3} />
              </div>
              <span className="text-white">{s}</span>
            </motion.li>
          ))}
        </ul>

        {/* CTA */}
        <motion.button
          onClick={onSelectPro || (() => {})}
          whileTap={{ scale: 0.97 }}
          data-testid="plan-cta-pro"
          className="w-full px-5 py-3.5 rounded-full font-semibold text-[15px] inline-flex items-center justify-center gap-2"
          style={{
            background: "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)",
            color: "white",
            boxShadow: "0 14px 32px -8px rgba(255,122,26,0.75)",
          }}
        >
          <Sparkles className="h-4 w-4" /> Prova 30 giorni gratis
        </motion.button>
        <div className="text-center text-[10.5px] text-white/55 mt-3">
          Nessuna carta richiesta ora · ti avvisiamo prima del rinnovo
        </div>
      </div>
    </motion.div>
  );
}

/* FREE — clean, calm card */
function FreeCard({ onSelectFree }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: 10 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.96, y: -8 }}
      transition={{ duration: 0.35 }}
      data-testid="plan-free-card"
      className="relative rounded-[28px] overflow-hidden border-2 border-[#2DBA60] bg-white"
      style={{ boxShadow: "0 18px 46px -16px rgba(45,186,96,0.40), 0 2px 10px -2px rgba(0,0,0,0.06)" }}
    >
      <motion.div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        animate={{ opacity: [0.55, 0.85, 0.55] }}
        transition={{ duration: 3.2, repeat: Infinity }}
        style={{ background: "radial-gradient(70% 80% at 100% 0%, rgba(45,186,96,0.15) 0%, transparent 60%)" }}
      />
      <div className="relative z-10 p-6 sm:p-7">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <div
              className="h-9 w-9 rounded-xl flex items-center justify-center"
              style={{
                background: "linear-gradient(135deg, #2DBA60, #48D478)",
                boxShadow: "0 6px 16px -4px rgba(45,186,96,0.5)",
              }}
            >
              <ShieldCheck className="h-5 w-5 text-white" strokeWidth={2.4} />
            </div>
            <span className="font-display text-[20px] sm:text-[22px] tracking-tight text-[#1A1A1A]">
              ORIO Free
            </span>
          </div>
          <div
            className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[#E0F4E6] text-[#2DBA60]"
          >
            Per sempre
          </div>
        </div>

        <div className="flex items-baseline gap-2 mb-1">
          <span className="font-display text-[44px] sm:text-[52px] leading-none text-[#1A1A1A]">€0</span>
          <span className="text-[14px] text-[#9A9A9A]">/ sempre</span>
        </div>
        <div className="text-[12px] text-[#6B6B6B] mb-6">Le basi che servono a ogni padrone</div>

        <ul className="space-y-2.5 mb-7">
          {FREE_SUMMARY.map((s, i) => (
            <motion.li
              key={i}
              initial={{ opacity: 0, x: -6 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 + i * 0.08 }}
              className="flex items-center gap-2.5 text-[13.5px]"
            >
              <div
                className="h-5 w-5 rounded-full flex items-center justify-center shrink-0"
                style={{
                  background: "linear-gradient(135deg, #2DBA60, #48D478)",
                  boxShadow: "0 3px 10px -2px rgba(45,186,96,0.45)",
                }}
              >
                <Check className="h-3 w-3 text-white" strokeWidth={3} />
              </div>
              <span className="text-[#1A1A1A]">{s}</span>
            </motion.li>
          ))}
        </ul>

        {onSelectFree ? (
          <motion.button
            onClick={onSelectFree}
            whileTap={{ scale: 0.97 }}
            data-testid="plan-cta-free"
            className="w-full px-5 py-3.5 rounded-full font-semibold text-[15px] text-white inline-flex items-center justify-center gap-2"
            style={{
              background: "linear-gradient(135deg, #2DBA60, #48D478)",
              boxShadow: "0 12px 28px -8px rgba(45,186,96,0.55)",
            }}
          >
            Comincia gratis
          </motion.button>
        ) : (
          <Link
            to="/registrati"
            data-testid="plan-cta-free"
            className="block w-full px-5 py-3.5 rounded-full font-semibold text-[15px] text-center text-white"
            style={{
              background: "linear-gradient(135deg, #2DBA60, #48D478)",
              boxShadow: "0 12px 28px -8px rgba(45,186,96,0.55)",
            }}
          >
            Comincia gratis
          </Link>
        )}
        <div className="text-center text-[10.5px] text-[#9A9A9A] mt-3">
          Puoi sempre passare a Pro quando vuoi
        </div>
      </div>
    </motion.div>
  );
}

/* Inline compact upsell — used in Home for Free users */
function InlineCard({ onSelectPro }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      data-testid="plan-inline"
      className="relative rounded-[24px] overflow-hidden border-2 border-[#FF7A1A]"
      style={{
        background: "linear-gradient(155deg, #1A1A1A 0%, #2A1810 100%)",
        boxShadow: "0 18px 40px -12px rgba(255,122,26,0.45)",
      }}
    >
      <motion.div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        animate={{
          background: [
            "radial-gradient(60% 80% at 100% 0%, rgba(255,122,26,0.4), transparent 60%)",
            "radial-gradient(60% 80% at 0% 100%, rgba(244,184,60,0.4), transparent 60%)",
            "radial-gradient(60% 80% at 100% 0%, rgba(255,122,26,0.4), transparent 60%)",
          ],
        }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
      />
      <div className="relative z-10 p-5 text-white">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <motion.div
              animate={{ rotate: [0, -8, 8, 0] }}
              transition={{ duration: 1.8, repeat: Infinity, repeatDelay: 2 }}
            >
              <Crown className="h-5 w-5 text-[#FF7A1A]" strokeWidth={2.4} />
            </motion.div>
            <span className="font-display text-[18px]">ORIO Pro</span>
          </div>
          <motion.span
            animate={{ scale: [1, 1.06, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="px-2 py-0.5 rounded-full text-[9.5px] font-bold uppercase tracking-wider"
            style={{ background: "linear-gradient(135deg, #2DBA60, #48D478)", color: "white" }}
          >
            30 giorni gratis
          </motion.span>
        </div>
        <div className="font-display text-[24px] leading-tight mb-1">
          Sblocca tutto ORIO
        </div>
        <div className="text-[12px] text-white/70 mb-4">
          SOS, promemoria, match, missioni di salvataggio
        </div>
        <motion.button
          onClick={onSelectPro || (() => {})}
          whileTap={{ scale: 0.97 }}
          data-testid="plan-inline-cta"
          className="w-full px-5 py-2.5 rounded-full font-semibold text-[13px] text-white inline-flex items-center justify-center gap-2"
          style={{
            background: "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)",
            boxShadow: "0 8px 20px -4px rgba(255,122,26,0.65)",
          }}
        >
          <Sparkles className="h-3.5 w-3.5" /> Prova 30 giorni gratis
        </motion.button>
      </div>
    </motion.div>
  );
}
